import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function MasterCustomServices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quotes/custom-services");
      setRows(r.data || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    const price = parseFloat(newPrice) || 0;
    if (!name) return toast.error("Nome obrigatório");
    if (price <= 0) return toast.error("Preço deve ser maior que zero");
    try {
      await api.post("/quotes/custom-services", { name, default_price: price, notes: newNotes.trim() || null });
      toast.success("Serviço criado");
      setNewName(""); setNewPrice(""); setNewNotes(""); setCreating(false);
      load();
    } catch (e) { toast.error("Falha ao criar"); }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({ name: row.name, default_price: row.default_price, notes: row.notes || "" });
  };

  const saveEdit = async () => {
    const name = (editDraft.name || "").trim();
    const price = parseFloat(editDraft.default_price) || 0;
    if (!name) return toast.error("Nome obrigatório");
    if (price <= 0) return toast.error("Preço deve ser maior que zero");
    try {
      await api.patch(`/quotes/custom-services/${editingId}`, {
        name,
        default_price: price,
        notes: (editDraft.notes || "").trim() || null,
      });
      toast.success("Atualizado");
      setEditingId(null); setEditDraft({});
      load();
    } catch (e) { toast.error("Falha ao salvar"); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remover "${row.name}"? Não afeta orçamentos já criados.`)) return;
    try {
      await api.delete(`/quotes/custom-services/${row.id}`);
      toast.success("Removido");
      load();
    } catch (e) { toast.error("Falha ao remover"); }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="custom-services-page">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-600/15 border border-amber-500/20 flex items-center justify-center">
            <Wrench className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Master Data</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Serviços Customizados</h1>
            <p className="text-sm text-slate-400 mt-1">Serviços extras que aparecem na lista de operações dos orçamentos (Diagnóstico OBD, Reset SAS, Test Drive...).</p>
          </div>
        </div>
        {!creating && (
          <Button data-testid="new-custom-btn" onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-500">
            <Plus className="w-4 h-4 mr-2" /> Novo Serviço
          </Button>
        )}
      </div>

      {/* Create form */}
      {creating && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="create-form">
          <div className="grid grid-cols-[1fr_160px] gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Nome</Label>
              <Input data-testid="new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ex: Diagnóstico OBD" className="mt-1 bg-[#030712] border-slate-800" autoFocus />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Preço padrão ($)</Label>
              <Input data-testid="new-price" type="number" step="0.01" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="0.00" className="mt-1 bg-[#030712] border-slate-800 font-mono text-right" />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Notas (opcional)</Label>
            <Input data-testid="new-notes" value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Descrição rápida" className="mt-1 bg-[#030712] border-slate-800" />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setCreating(false); setNewName(""); setNewPrice(""); setNewNotes(""); }} className="border-slate-700"><X className="w-4 h-4 mr-1" />Cancelar</Button>
            <Button data-testid="create-submit-btn" onClick={create} className="bg-amber-600 hover:bg-amber-500"><Save className="w-4 h-4 mr-1" />Salvar</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-900/50 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-right px-4 py-3 w-40">Preço Padrão</th>
              <th className="text-left px-4 py-3">Notas</th>
              <th className="text-right px-4 py-3 w-32">Criado</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center text-slate-500 py-10">Carregando...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-500 py-10" data-testid="no-services">
                Nenhum serviço customizado ainda. Você também pode criar direto num orçamento clicando &quot;Adicionar Serviço Manual&quot; e escolhendo &quot;Salvar na lista&quot;.
              </td></tr>
            )}
            {rows.map((r) => {
              const isEditing = editingId === r.id;
              return (
                <tr key={r.id} data-testid={`service-row-${r.id}`} className="border-t border-slate-800/60 hover:bg-slate-900/30 transition">
                  <td className="px-4 py-2.5">
                    {isEditing ? (
                      <Input data-testid={`edit-name-${r.id}`} value={editDraft.name || ""} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="bg-[#030712] border-slate-800 h-8" />
                    ) : (
                      <div className="text-sm font-medium text-slate-100">{r.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {isEditing ? (
                      <Input data-testid={`edit-price-${r.id}`} type="number" step="0.01" value={editDraft.default_price ?? ""} onChange={(e) => setEditDraft({ ...editDraft, default_price: e.target.value })} className="bg-[#030712] border-slate-800 h-8 font-mono text-right" />
                    ) : (
                      <span className="font-mono text-amber-300">${(r.default_price || 0).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-400">
                    {isEditing ? (
                      <Input data-testid={`edit-notes-${r.id}`} value={editDraft.notes || ""} onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })} className="bg-[#030712] border-slate-800 h-8" />
                    ) : (
                      r.notes || <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 text-right font-mono">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      {isEditing ? (
                        <>
                          <button data-testid={`save-btn-${r.id}`} onClick={saveEdit} className="text-emerald-400 hover:text-emerald-300 p-1"><Save className="w-4 h-4" /></button>
                          <button onClick={() => { setEditingId(null); setEditDraft({}); }} className="text-slate-500 hover:text-slate-300 p-1"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button data-testid={`edit-btn-${r.id}`} onClick={() => startEdit(r)} className="text-slate-500 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`delete-btn-${r.id}`} onClick={() => remove(r)} className="text-slate-500 hover:text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
